import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpErrorResponse,
  HttpRequest,
} from '@angular/common/http';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { catchError, switchMap, take, throwError } from 'rxjs';
import { TokenRefreshService } from '../services/token-refresh.service';

/**
 * On a 401, refreshes the session once and retries the request with the fresh
 * token. If the session cannot be recovered the ORIGINAL 401 is propagated.
 *
 * Two things this deliberately does NOT do, both of which it used to:
 *
 * - It does not return `EMPTY` on an unrecoverable 401. An observable that
 *   neither emits nor errors leaves `userDataGuard` waiting forever: its `map`
 *   and `catchError` never run, the guard completes without a value, and
 *   Angular silently cancels the navigation — a blank page with the loading
 *   overlay still up. Callers must be told the request failed.
 *
 * - It does not call `logoff()`. Beyond being the wrong call to make from an
 *   interceptor (it navigates to the IdP end-session endpoint, and
 *   `postLogoutRedirectUri` is the app origin, so the browser lands back on the
 *   guarded route tree), `OidcSecurityService.logoff()` returns a COLD
 *   observable: the old code invoked it without subscribing, so it never
 *   actually logged anyone off. Deciding to end the session belongs to a guard
 *   or a component, not here.
 */
export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const oidc = inject(OidcSecurityService);
  const tokenRefresh = inject(TokenRefreshService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      return tokenRefresh.refresh().pipe(
        // The refresh failing is not itself interesting to the caller — what
        // matters is that the request stays unauthorized.
        catchError(() => throwError(() => error)),
        switchMap((loginResponse) => {
          if (!loginResponse?.isAuthenticated) {
            return throwError(() => error);
          }

          // The original request still carries the expired Authorization
          // header set by authInterceptor (upstream in the chain), so the
          // retry must be re-signed with the freshly refreshed token.
          return oidc.getAccessToken().pipe(
            take(1),
            switchMap((token) => next(withBearer(req, token))),
          );
        }),
      );
    }),
  );
};

function withBearer(
  req: HttpRequest<unknown>,
  token: string | null,
): HttpRequest<unknown> {
  return token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;
}
